import React, { useCallback, useEffect, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  ModalBody,
  Spinner
} from 'reactstrap';
import Button from '../../form/Button';
import cx from 'classnames';
import { replaceParamsInUrl } from '../../../utils';
import { FormattedMessage } from 'react-intl';

const REQUEST_TIMEOUT = 4000;
const REQUEST_RETRIES = 3;

const PurchaseModal = ({
  image,
  title,
  code,
  transaction,
  isOpenModal,
  onToggleModal,
  description,
  errorDescription,
  primaryButton,
  secondaryButton,
  activateButton,
  descriptionImage,
  productsToBuy,
  getDisneySubscriber,
  client,
  contentProvider,
  redirectToCloudPayUrl,
  disneySubscription,
  country,
  cleanOrder
}) => {
  // addOns are added to a local state to persist them across redirection and cleanup
  const [addOns] = useState(productsToBuy);
  const mergedAddOns = Object.entries(addOns || []).reduce((acc, item) => acc.concat(item[1]), []);
  const addOnsToActivate = mergedAddOns.filter(pkg => pkg?.metadata?.activationUrl);
  const isLinkedUser = !!(disneySubscription?.subscription?.activated === 'LINKED');
  const [finishRetry, setFinishRetry] = useState(false);

  const requestWithRetry = useCallback(async () => {
    const timeout = delay => {
      return new Promise(resolve => setTimeout(resolve, delay))
    }
    if (!disneySubscription) {
      for (let retries = 0; retries < REQUEST_RETRIES; ++retries) {
        if (disneySubscription) {
          setFinishRetry(true);
          break;
        }
        await timeout(REQUEST_TIMEOUT);
        await getDisneySubscriber();
        await timeout(REQUEST_TIMEOUT);
        if (retries === (REQUEST_RETRIES - 1)) {
          setFinishRetry(true);
        }
      }
    }
    setFinishRetry(true);
  }, [disneySubscription]);

  useEffect(() => {
    if (addOnsToActivate.length > 0) {
      requestWithRetry();
    }
  }, []);

  const handleRedirectToActivate = (pkg) => {
    const returnUrl = replaceParamsInUrl(pkg.metadata.activationUrl, { client, country });
    const paramsToReplace = {
      cp: contentProvider,
      idp: client,
      returnUrl,
      jwt: localStorage.getItem('jwt')
    };
    const url = replaceParamsInUrl(redirectToCloudPayUrl, paramsToReplace);
    window.location.href = url;
    // reset redux state of selected addOns
    cleanOrder();
  };
  const handleConfirm = (fn) => {
    fn();
    if (code === 200) {
      cleanOrder();
    }
  };

  return (
    <Modal
      className="crm-modal"
      isOpen={isOpenModal}
      toggle={onToggleModal}
      fade={false}
      centered
    >
      <ModalBody>
        <div className="PaymentStatus">
          <div className="PaymentStatus__main">
            {image && (
              <div className="image">
                {image}
              </div>
            )}
            <span className="PaymentStatus__title PaymentStatus--muted">
              {title}
            </span>

            {descriptionImage && (
              <span className="PaymentStatus__subtitle PaymentStatus--muted">
                {descriptionImage}
              </span>
            )}
            <p className="PaymentStatus__subtitle PaymentStatus--muted">{description}</p>
            {addOnsToActivate.length > 0 && code === 200 && (
              addOnsToActivate.map((pkg, i) => (
                <Fragment key={i}>
                  <div className="PaymentStatus__activation">
                    <p className="PaymentStatus__subtitle PaymentStatus--muted">{pkg?.metadata?.activationDescription}</p>
                    <img
                      className="PaymentStatus__activation-img"
                      src={pkg.metadata?.image}
                      alt="description image"
                    />
                  </div>
                  <div className="PaymentStatus__button-container">
                    <Button
                      className="forms-Button" type="button"
                      isLoading={!finishRetry}
                      disabled={Boolean(!disneySubscription) || !finishRetry}
                      onClick={() => handleRedirectToActivate(pkg)}
                      loadingComponent={() => (
                        <Spinner xsmall />
                      )}
                    >
                      {(!isLinkedUser && finishRetry) ? activateButton?.text.unlinked : activateButton?.text.linked}
                    </Button>
                  </div>
                </Fragment>
              ))
            )}
            {primaryButton && (
              <div className="PaymentStatus__button-container">
                <Button
                  className="forms-Button" type="button"
                  onClick={() => handleConfirm(primaryButton?.onClick)}
                >
                  {primaryButton?.text}
                </Button>
              </div>
            )}
            {secondaryButton && (
              <div className="PaymentStatus__button-container">
                <Button
                  className={cx('forms-Button', {
                    empty: (addOnsToActivate.length > 0)
                  })}
                  type="button"
                  onClick={() => handleConfirm(secondaryButton?.onClick)}
                >
                  {secondaryButton?.text}
                </Button>
              </div>
            )}
            {code && errorDescription && code !== 200 && (
              <span className="PaymentStatus__status PaymentStatus--muted">
                {errorDescription}
              </span>
            )}
            {
              finishRetry && Boolean(!disneySubscription) && (
                <span className="PaymentStatus__status PaymentStatus__activation-err">
                  <FormattedMessage
                    id="PaymentStatus.activation.disney.error"
                    values={{
                      transactionId: transaction.originalExternalId
                    }}
                  />
                </span>
              )
            }
          </div>
        </div>
      </ModalBody>
    </Modal>
  )
};
PurchaseModal.propTypes = {
  image: PropTypes.any,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  isOpenModal: PropTypes.bool,
  onToggleModal: PropTypes.func,
  code: PropTypes.number,
  transaction: PropTypes.object,
  productsToBuy: PropTypes.array,
  client: PropTypes.string,
  country: PropTypes.string,
  contentProvider: PropTypes.string,
  redirectToCloudPayUrl: PropTypes.string,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  errorDescription: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  descriptionImage: PropTypes.any,
  disneySubscription: PropTypes.object,
  activateButton: PropTypes.shape({
    text: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    isNeutral: PropTypes.bool,
    onClick: PropTypes.func,
  }),
  primaryButton: PropTypes.shape({
    text: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    isNeutral: PropTypes.bool,
    onClick: PropTypes.func,
    customClass: PropTypes.string
  }),
  secondaryButton: PropTypes.shape({
    text: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    isNeutral: PropTypes.bool,
    onClick: PropTypes.func,
    customClass: PropTypes.string
  }),
  getDisneySubscriber: PropTypes.func,
  cleanOrder: PropTypes.func
}
export default PurchaseModal;
